import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import servicesData from "@/data/servicesData";
import axios from "axios";
import {
  Building2,
  ArrowRight,
  Shield,
  UserCheck,
  Star,
  Lock,
  ChevronDown,
  CheckCircle2,
  Clock,
  Phone,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 px-1 text-left hover:bg-slate-50/50 transition-colors">
        <span className="font-semibold text-[#0B1F3B] text-sm pr-4">{question}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-5 px-1"><p className="text-sm text-slate-600 leading-relaxed">{answer}</p></div>}
    </div>
  );
}

const TRUST_ICONS = [Shield, UserCheck, Lock, Star];

const TESTIMONIALS = [
  { name: "Sarah M.", location: "Dallas, TX", rating: 5, text: "Found a great contractor within 48 hours. The whole process was seamless and I felt in control the entire time.", avatar: "SM" },
  { name: "Mike R.", location: "Fort Worth, TX", rating: 5, text: "No spam calls, no strangers showing up. Just real bids from professionals who actually wanted the job.", avatar: "MR" },
  { name: "Jessica L.", location: "Plano, TX", rating: 5, text: "Saved thousands by comparing bids side-by-side. The contractor I hired did incredible work and finished on time.", avatar: "JL" },
];

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const service = servicesData.find((s) => s.slug === slug);
  const [images, setImages] = useState(null);
  const [showMobileCta, setShowMobileCta] = useState(false);

  useEffect(() => {
    if (service) {
      document.title = service.metaTitle;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", service.metaDesc);
    }
    window.scrollTo(0, 0);
  }, [slug, service]);

  useEffect(() => {
    if (slug) {
      axios.get(`${API}/service-images/${slug}`).then((res) => setImages(res.data)).catch(() => {});
    }
  }, [slug]);

  useEffect(() => {
    const onScroll = () => setShowMobileCta(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0B1F3B] mb-4">Service not found</h1>
          <Link to="/"><Button className="bg-[#0B1F3B] text-white">Back to Home</Button></Link>
        </div>
      </div>
    );
  }

  const ServiceIcon = service.icon;
  const heroImage = images?.hero_url || service.heroImage;
  const title = service.title;
  const heroHeadline = service.heroHeadline;
  const heroSub = service.heroSub;
  const trustPoints = service.trustPoints;
  const processSteps = service.process;
  const included = service.included;
  const persuasiveCopy = service.persuasiveCopy;
  const faqs = service.faqs;
  const gallery = images?.gallery || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navbar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <span className="font-bold text-[#0B1F3B]">{t("common.appName")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login"><Button variant="ghost" size="sm">{t("common.logIn")}</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white">{t("common.getStarted")}</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-[#0B1F3B] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt={`${title} contractor at work`} className="w-full h-full object-cover opacity-20" width={1200} height={800} loading="eager" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <ServiceIcon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <span className="text-[#D4AF37] font-semibold">{title}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6" style={{ fontFamily: "Manrope" }}>
              {heroHeadline}
            </h1>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl">{heroSub}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate("/register")} className="gold-gradient text-[#0B1F3B] hover:brightness-110 font-extrabold px-8 py-6 text-lg shadow-lg" data-testid="service-hero-cta">
                {t("landing.getFreeEstimates")}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button variant="outline" onClick={() => navigate("/register?type=business")} className="border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg">
                {t("landing.joinAsBusiness")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Points */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B] text-center mb-12" style={{ fontFamily: "Manrope" }}>
            Why Homeowners Choose Verified {title} Pros
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustPoints.map((point, idx) => {
              const TrustIcon = TRUST_ICONS[idx] || Shield;
              return (
                <div key={idx} className="text-center p-6 rounded-xl bg-[#F8FAFC] border border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <TrustIcon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <p className="text-sm font-medium text-slate-700 leading-relaxed">{point}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 md:py-20 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B] text-center mb-12" style={{ fontFamily: "Manrope" }}>
            What Homeowners Are Saying
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((review, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4 italic">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#0B1F3B] flex items-center justify-center text-white text-xs font-bold">{review.avatar}</div>
                  <div>
                    <p className="text-sm font-semibold text-[#0B1F3B]">{review.name}</p>
                    <p className="text-xs text-slate-500">{review.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process — 3 Steps */}
      <section className="py-16 md:py-20 bg-[#0B1F3B]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white text-center mb-12" style={{ fontFamily: "Manrope" }}>
            {t("landing.howItWorksTitle")}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-extrabold text-[#D4AF37]">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B] text-center mb-12" style={{ fontFamily: "Manrope" }}>
            What {title} Services Cover
          </h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {included.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC]">
                <div className="w-2 h-2 rounded-full bg-[#D4AF37] flex-shrink-0" />
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Work Gallery */}
      {gallery.length > 0 && (
        <section className="py-16 md:py-20 bg-[#F8FAFC]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B] text-center mb-12" style={{ fontFamily: "Manrope" }}>
              Recent {title} Work
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((img, idx) => (
                <div key={img.id} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm aspect-[4/3]">
                  <img
                    src={img.url}
                    alt={img.label || `${title} project ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    width={400}
                    height={300}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Persuasive Copy */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#F8FAFC] rounded-2xl border border-slate-200 p-8 md:p-12">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <ServiceIcon className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider">Why Act Now</span>
            </div>
            <p className="text-slate-700 leading-relaxed text-base">{persuasiveCopy}</p>
          </div>
        </div>
      </section>

      {/* Trust / Urgency Strip */}
      <section className="py-10 bg-[#0B1F3B]/5 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#0B1F3B]">Contact goes to one contractor — no spam calls</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#0B1F3B]">Licensed & insured contractors only</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#0B1F3B]">Most requests typically get 3+ bids within 24 hours</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B] text-center mb-12" style={{ fontFamily: "Manrope" }}>
            {title} — Frequently Asked Questions
          </h2>
          <div className="bg-white rounded-xl border border-slate-200 px-6">
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16 md:py-20 bg-[#0B1F3B]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Manrope" }}>
            Ready to Get {title} Estimates?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Post your project for free. Verified {title.toLowerCase()} contractors in your area will send you competitive bids. No spam, no obligation.
          </p>
          <Button onClick={() => navigate("/register")} className="gold-gradient text-[#0B1F3B] hover:brightness-110 font-extrabold px-10 py-6 text-lg shadow-lg" data-testid="service-closing-cta">
            {t("landing.startGettingEstimates")}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-5xl mx-auto px-6 text-center text-sm text-slate-500">
          &copy; {new Date().getFullYear()} {t("common.appName")}. {t("landing.allRightsReserved")}
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      {showMobileCta && (
        <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-slate-200 p-3 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]" data-testid="sticky-mobile-cta">
          <Button onClick={() => navigate("/register")} className="w-full gold-gradient text-[#0B1F3B] hover:brightness-110 font-extrabold h-12 text-base shadow-lg">
            {t("landing.getFreeEstimates")}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      )}
    </div>
  );
}
